import React from 'react';
import { useCourseList, useCourseGet } from '@/hooks/queries/course/useList';
import { Course } from '@/types/courses';
import { getCourseTitle, getCourseImage } from '@/utils/common';
import { BookOpen } from 'lucide-react';

interface CourseCellProps {
  courseId?: string | null;
  course?: Partial<Course> | null;
  fallbackText?: string;
}

const extractCourses = (data: any): Course[] => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.data?.data)) return data.data.data;
  if (Array.isArray(data?.data?.items)) return data.data.items;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.courses)) return data.courses;
  return [];
};

export const CourseCell: React.FC<CourseCellProps> = ({
  courseId,
  course,
  fallbackText = 'Umumiy / Bog\'lanmagan',
}) => {
  const { data: coursesData, isLoading: isListLoading } = useCourseList();
  const courses = extractCourses(coursesData);

  const foundCourseInList = courseId
    ? courses.find((c: any) =>
        (c.id && String(c.id).toLowerCase() === String(courseId).toLowerCase()) ||
        (c.courseId && String(c.courseId).toLowerCase() === String(courseId).toLowerCase()) ||
        (c._id && String(c._id).toLowerCase() === String(courseId).toLowerCase())
      )
    : null;

  // Single course fallback query if not in the list
  const { data: singleCourseData, isLoading: isSingleLoading } = useCourseGet(
    !course && !foundCourseInList && courseId ? courseId : undefined
  );

  const targetCourse: any = course || foundCourseInList || singleCourseData;

  if (!targetCourse && !courseId) {
    return <span className="text-xs text-slate-400 italic">{fallbackText}</span>;
  }

  if (!targetCourse && (isListLoading || isSingleLoading)) {
    return (
      <div className="flex items-center gap-2">
        <div className="size-8 rounded-lg bg-slate-100 animate-pulse shrink-0" />
        <div className="space-y-1">
          <div className="h-3 w-20 bg-slate-100 rounded animate-pulse" />
          <div className="h-2 w-12 bg-slate-100 rounded animate-pulse" />
        </div>
      </div>
    );
  }

  const rawTitle = getCourseTitle(targetCourse);
  const title = rawTitle || (targetCourse?.format ? `${targetCourse.format} Kursi` : (courseId ? `Kurs (${courseId.slice(0, 8)}...)` : fallbackText));
  const imageSrc = getCourseImage(targetCourse);

  return (
    <div className="flex items-center gap-2.5 max-w-[260px]">
      <div className="size-8 rounded-lg bg-slate-100 border border-slate-200/60 overflow-hidden shrink-0 flex items-center justify-center">
        {imageSrc ? (
          <img
            src={imageSrc}
            alt={title}
            className="w-full h-full object-cover"
            onError={(e) => {
              (e.target as HTMLElement).style.display = 'none';
            }}
          />
        ) : (
          <BookOpen className="size-4 text-slate-400" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold text-slate-900 truncate" title={title}>
          {title}
        </p>
        {targetCourse?.format ? (
          <span className="text-[10px] text-primary font-medium">{targetCourse.format}</span>
        ) : (
          <span className="text-[10px] text-slate-400">Kurs</span>
        )}
      </div>
    </div>
  );
};
